"use client"

import { isEqual } from "lodash"
import { useParams } from "next/navigation"
import { useMemo, useRef, useState } from "react"
import { Minus, Plus, ShoppingCart } from "lucide-react"
import { useIntersection } from "@lib/hooks/use-in-view"
import { addToCart } from "@lib/data/cart"
import { emitCartUpdate, emitCartDelta } from "@lib/cart-event"
import { getProductPrice } from "@lib/util/get-product-price"
import { HttpTypes } from "@medusajs/types"
import { clx } from "@medusajs/ui"

type Props = {
  product: HttpTypes.StoreProduct
  region: HttpTypes.StoreRegion
  disabled?: boolean
}

const optionsAsKeymap = (variantOptions: any) => {
  return variantOptions?.reduce((acc: Record<string, string | undefined>, varopt: any) => {
    if (varopt.option && varopt.value !== null && varopt.value !== undefined) {
      acc[varopt.option.title] = varopt.value
    }
    return acc
  }, {})
}

export default function PetSelectProductActions({ product, region, disabled }: Props) {
  const [options, setOptions] = useState<Record<string, string | undefined>>(() => {
    if (product.variants?.length === 1) {
      return optionsAsKeymap(product.variants[0].options) ?? {}
    }
    return {}
  })
  const [isAdding, setIsAdding] = useState(false)
  const [quantity, setQuantity] = useState(1)
  const [cartError, setCartError] = useState<string | null>(null)
  const countryCode = useParams().countryCode as string
  const actionsRef = useRef<HTMLDivElement>(null)
  const inView = useIntersection(actionsRef, "0px")

  const selectedVariant = useMemo(() => {
    if (!product.variants || product.variants.length === 0) return undefined
    return product.variants.find((v) => {
      const variantOptions = optionsAsKeymap(v.options)
      return isEqual(variantOptions, options)
    })
  }, [product.variants, options])

  const inStock = useMemo(() => {
    if (selectedVariant && !selectedVariant.manage_inventory) return true
    if (selectedVariant?.allow_backorder) return true
    if (selectedVariant?.manage_inventory && (selectedVariant?.inventory_quantity || 0) > 0) return true
    return false
  }, [selectedVariant])

  const { cheapestPrice, variantPrice } = getProductPrice({ product, variantId: selectedVariant?.id })
  const selectedPrice = selectedVariant ? variantPrice : cheapestPrice
  const hasDiscount = selectedPrice?.price_type === "sale"

  const handleAddToCart = async () => {
    if (!selectedVariant?.id) return
    setIsAdding(true)
    setCartError(null)
    // Optimistically update badge immediately — don't wait for server
    emitCartDelta(quantity)
    try {
      await addToCart({ variantId: selectedVariant.id, quantity, countryCode })
      emitCartUpdate()
    } catch (err: any) {
      // Revert optimistic delta on failure
      emitCartDelta(-quantity)
      console.error('[handleAddToCart]', err)
      setCartError(err?.message || 'Failed to add to cart. Please try again.')
    } finally {
      setIsAdding(false)
    }
  }

  return (
    <div className="flex flex-col gap-6" ref={actionsRef}>
      {/* Price */}
      <div className="flex items-baseline gap-3 flex-wrap">
        {selectedPrice && (
          <>
            <span
              className={clx("text-3xl font-bold", {
                "text-[#FF6B35]": hasDiscount,
                "text-gray-900": !hasDiscount,
              })}
              data-testid="product-price"
            >
              {!selectedVariant && "From "}
              {selectedPrice.calculated_price}
            </span>
            {hasDiscount && (
              <>
                <span className="text-lg text-gray-400 line-through" data-testid="original-product-price">
                  {selectedPrice.original_price}
                </span>
                <span className="bg-[#FF6B35] text-white text-xs font-bold px-2.5 py-1 rounded-full">
                  SAVE {selectedPrice.percentage_diff}%
                </span>
              </>
            )}
          </>
        )}
      </div>

      {/* Variant Options */}
      {(product.variants?.length ?? 0) > 1 && (
        <div className="space-y-4">
          {(product.options || []).map((option) => (
            <div key={option.id}>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                {option.title}
              </label>
              <div className="flex flex-wrap gap-2">
                {option.values?.map((val) => {
                  const isSelected = options[option.title ?? ""] === val.value
                  return (
                    <button
                      key={val.id}
                      onClick={() => option.title && setOptions(prev => ({ ...prev, [option.title!]: val.value }))}
                      disabled={!!disabled || isAdding}
                      className={clx(
                        "px-4 py-2.5 rounded-lg text-sm font-medium border-2 transition-all",
                        isSelected
                          ? "border-[#0B7A75] bg-[#0B7A75]/5 text-[#0B7A75]"
                          : "border-gray-200 text-gray-700 hover:border-gray-400"
                      )}
                    >
                      {val.value}
                    </button>
                  )
                })}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Stock Status */}
      <div>
        {!selectedVariant ? (
          <span className="text-sm text-gray-500">Select options above</span>
        ) : inStock ? (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-green-700">
            <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
            In Stock — Ready to Ship
          </span>
        ) : (
          <span className="inline-flex items-center gap-1.5 text-sm font-medium text-red-600">
            <span className="w-2 h-2 bg-red-500 rounded-full" />
            Out of Stock
          </span>
        )}
      </div>

      {/* Quantity + Add to Cart */}
      <div className="flex items-center gap-3">
        <div className="flex items-center border border-gray-300 rounded-lg overflow-hidden">
          <button
            onClick={() => setQuantity(Math.max(1, quantity - 1))}
            className="p-3 hover:bg-gray-50 transition-colors"
            disabled={!!disabled || isAdding}
          >
            <Minus size={16} />
          </button>
          <span className="px-5 text-base font-semibold min-w-[48px] text-center">{quantity}</span>
          <button
            onClick={() => setQuantity(quantity + 1)}
            className="p-3 hover:bg-gray-50 transition-colors"
            disabled={!!disabled || isAdding}
          >
            <Plus size={16} />
          </button>
        </div>

        <button
          onClick={handleAddToCart}
          disabled={!inStock || !selectedVariant || !!disabled || isAdding}
          className={clx(
            "flex-1 flex items-center justify-center gap-2 py-3.5 px-6 rounded-lg text-base font-bold transition-all",
            !inStock || !selectedVariant || !!disabled
              ? "bg-gray-200 text-gray-400 cursor-not-allowed"
              : "bg-[#0B7A75] hover:bg-[#065A55] text-white shadow-lg shadow-[#0B7A75]/25 hover:shadow-xl"
          )}
          data-testid="add-product-button"
        >
          {isAdding ? (
            <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
          ) : (
            <>
              <ShoppingCart size={20} />
              {!selectedVariant ? "Select Options" : !inStock ? "Out of Stock" : "Add to Cart"}
            </>
          )}
        </button>
      </div>

      {cartError && (
        <p className="text-red-600 text-sm px-1 mt-1">{cartError}</p>
      )}

      {/* Mobile sticky bar */}
      {!inView && selectedVariant && inStock && (
        <div className="fixed bottom-0 left-0 right-0 z-50 bg-white border-t border-gray-200 p-4 shadow-2xl lg:hidden">
          <div className="flex items-center gap-3 max-w-lg mx-auto">
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 truncate">{product.title}</p>
              <p className="text-lg font-bold text-[#0B7A75]">{selectedPrice?.calculated_price}</p>
            </div>
            <button
              onClick={handleAddToCart}
              disabled={isAdding}
              className="bg-[#0B7A75] text-white py-3 px-6 rounded-lg font-bold flex items-center gap-2"
            >
              <ShoppingCart size={18} />
              Add
            </button>
          </div>
        </div>
      )}
    </div>
  )
}
